import { useTranslation } from 'react-i18next'
import { formatNumbers } from 'core/helpers/format-numbers'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { stageSelector, selectedBetSelector } from 'models/battle/selectors'
import { stageType } from '../constants/stageType'
import { setStage, setSelectedBet, setBattlePlayers } from 'models/battle'
import { useAuthContext } from '../../../core/providers/AuthProvider'
import { battleService } from '../../../core/services'
import { useSocket } from '../../../core/hooks/useSocket'
import { useRef, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import c from './assets/sass/bet.module.scss'

const DEFAULT_BETS = [100, 500, 1000]

export const Bet = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const stage = useSelector(stageSelector)
    const selectedBet = useSelector(selectedBetSelector)
    const { user, updateUserData } = useAuthContext()
    const { on, joinBattle } = useSocket()
    const battleIdRef = useRef(null)
    const [bets, setBets] = useState(DEFAULT_BETS)

    useEffect(() => {
        battleService.getBetConfig()
            .then(data => {
                if (data?.bets?.length) {
                    setBets(data.bets)
                    const amounts = data.bets
                    if (!amounts.includes(selectedBet)) {
                        dispatch(setSelectedBet(amounts[0]))
                    }
                }
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!on) return

        const handleBattleStarted = (data) => {
            dispatch(setBattlePlayers(data?.players || []))
            dispatch(setStage(stageType.rolling))
        }

        const unsub = on('battle_started', handleBattleStarted)
        return () => unsub && unsub()
    }, [on, dispatch])

    useEffect(() => {
        const checkCurrentBattle = async () => {
            try {
                const res = await battleService.getCurrentBattle()

                if (res?.battle) {
                    const battle = res.battle
                    battleIdRef.current = battle.id

                    try {
                        joinBattle(battle.id)
                    } catch (e) {
                        console.error('Failed to join battle room:', e)
                    }

                    if (battle.status === 'WAITING') {
                        dispatch(setStage(stageType.waiting))
                    } else if (battle.status === 'ACTIVE') {
                        dispatch(setBattlePlayers(battle.players || []))
                        dispatch(setStage(stageType.rolling))
                    } else if (battle.status === 'FINISHED') {
                        dispatch(setStage(stageType.betting))
                    }
                }
            } catch (error) {
                console.error('Failed to check current battle:', error)
            }
        }
        checkCurrentBattle()
    }, [dispatch, joinBattle])

    const clickHandler = async () => {
        try {
            if (stage === stageType.betting && user?.balance >= selectedBet) {
                console.log('🎲 Creating/joining battle with bet:', selectedBet)

                const res = await battleService.createOrJoinBattle(selectedBet)
                console.log('🎲 Battle response:', res)

                if (res?.success && res?.battle?.id) {
                    battleIdRef.current = res.battle.id

                    // ВАЖНО: joinBattle ПЕРЕД сменой стейта
                    try {
                        joinBattle(res.battle.id)
                        console.log('✅ Joined battle room:', res.battle.id)
                    } catch (e) {
                        console.error('❌ Failed to join battle room:', e)
                    }

                    await updateUserData()

                    if (res.battle.status === 'WAITING') {
                        dispatch(setStage(stageType.waiting))
                    } else if (res.battle.status === 'ACTIVE') {
                        dispatch(setStage(stageType.rolling))
                    }
                }
            } else if (stage !== stageType.betting && battleIdRef.current) {
                try {
                    await battleService.cancelBattle(battleIdRef.current)
                    console.log('❌ Battle cancelled')
                } catch {}
                battleIdRef.current = null
                window.battleSpunRef = false
                dispatch(setStage(stageType.betting))
                await updateUserData()
            }
        } catch (error) {
            console.error('❌ Ошибка старта/отмены битвы:', error)
            const code = error.response?.data?.code
            let errorMsg
            if (code === 'SESSION_PROFIT_LIMIT_REACHED') {
                errorMsg = t('battle.bet.dailyLimit')
            } else {
                errorMsg = error.response?.data?.error || t('battle.bet.error')
            }
            toast(errorMsg, { type: 'error' })
        }
    }

    return (
        <div className={c.component}>
            <div className={c.wrapper}>
                <div className={c.row}>
                    <span className={c.title}>{t('battle.bet.title')}</span>
                    <span className={c.subTitle}>
                        {t('battle.bet.subtitle')}
                    </span>
                    <ul className={c.list}>
                        {bets.map((bet) => (
                            <li key={bet} className={c.item}>
                                <button
                                    className={cn(c.button, {
                                        [c.active]: selectedBet === bet,
                                        [c.disabled]: user?.balance < bet,
                                    })}
                                    onClick={() =>
                                        dispatch(setSelectedBet(bet))
                                    }
                                    disabled={user?.balance < bet}
                                >
                                    <span>{formatNumbers(bet)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <button
                        className={cn(c.btn, {
                            [c.start]: stage === stageType.betting,
                            [c.cancel]: stage !== stageType.betting,
                            [c.disabled]:
                                stage === stageType.betting &&
                                user?.balance < selectedBet,
                        })}
                        onClick={clickHandler}
                        disabled={
                            stage === stageType.betting &&
                            user?.balance < selectedBet
                        }
                    >
                        <span>
                            {stage === stageType.betting
                                ? user?.balance < selectedBet
                                    ? t('battle.bet.insufficient')
                                    : t('battle.bet.start')
                                : t('battle.bet.cancel')}
                        </span>
                    </button>
                </div>
            </div>
            <div
                className={cn(c.searching, {
                    [c.visible]: stage !== stageType.betting,
                })}
            >
                <div className={c.searchingRow}>
                    <div className={c.loader} />
                    <span>{t('battle.bet.searching')}</span>
                </div>
            </div>
        </div>
    )
}
